import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  FilterList as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { PLACEHOLDER_EVENT_IMAGE, DEFAULT_TIMEZONE } from '../config';
import { EventResponse } from '../services/eventService';
import ComingSoonTag from './ComingSoonTag';

export type Event = EventResponse;

interface EventListProps {
  events: Event[];
  onEventClick?: (event: Event) => void;
  baseUrl?: string;
}

const EventList: React.FC<EventListProps> = ({ 
  events,
  onEventClick,
  baseUrl = ''
}) => {



  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    };
    return date.toLocaleString('en-US', options);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      timeZone: DEFAULT_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (imageUrl?: string | null) => {
    if (!imageUrl) return PLACEHOLDER_EVENT_IMAGE;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${baseUrl}/uploads/events/${imageUrl}`;
  };

  return (
    <Box>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        sx={{ 
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          border: '1px dashed rgba(0, 0, 0, 0.1)'
        }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="center"
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          opacity: 0.6,
          pointerEvents: 'none'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary">
              Filter Events
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SortIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary">
              Sort Events
            </Typography>
          </Box>
          
          <ComingSoonTag variant="chip" size="medium" />
        </Box>
      </Stack>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 10, sm: 3 } }}>
        {events.map((event) => (
          <Grid item xs={12} sm={6} md={4} key={event.id}>
            <Card
              onClick={() => onEventClick?.(event)}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: 2,
                cursor: onEventClick ? 'pointer' : 'default',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={getImageUrl(event.imageUrl)}
                alt={event.title}
                sx={{ 
                  objectFit: 'cover',
                  height: { xs: 160, sm: 180, md: 200 },
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              />
              <CardContent sx={{ 
                flexGrow: 1,
                p: { xs: 1.5, sm: 2 },
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: { xs: 0.5, sm: 1 } 
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      lineHeight: 1.2,
                      fontWeight: 600,
                      color: 'text.primary',
                      letterSpacing: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      mr: 1
                    }}
                  >
                    {event.title}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.5,
                    mb: 0
                  }}
                >
                  {event.description}
                </Typography>
                <Box sx={{ 
                  mt: 'auto',
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: 'text.secondary'
                  }}>
                    <LocationIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        color: 'primary.light',
                        fontWeight: 600
                      }}
                    >
                      {event.venue.toUpperCase()}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: 'text.secondary',
                      flex: 1,
                      minWidth: '120px'
                    }}>
                      <CalendarIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          fontWeight: 500
                        }}
                      >
                        {formatDate(event.startTime)}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      color: 'text.secondary',
                      flex: 1,
                      minWidth: '120px'
                    }}>
                      <TimeIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.8rem' }
                        }}
                      >
                        {formatTime(event.startTime)} - {formatTime(event.endTime)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default EventList; 